// ICAO flight plan as far as the game needs it, plus callsign helpers.
import { spell, ALPHABET } from '../phraseology/spoken';

export interface FlightPlan {
  callsign: string;
  /** Radiotelephony designator, '' for registrations. */
  telephony: string;
  /** Flight number part of the callsign (e.g. '4TK'), or the registration without dash. */
  flightId: string;
  airline: string;
  type: string;
  rules: 'I' | 'V';
  dep: string;
  dest: string;
  route: string;
  /** Requested flight level (FL). */
  rfl: number;
  /** SID exit fix / STAR entry fix used for this flight. */
  exitFix?: string;
  entryFix?: string;
  /** Estimated off-block time and calculated take-off time (sim seconds). */
  eobt?: number;
  ctot?: number;
  /** Arrival: estimated time over the entry fix. */
  eta?: number;
  stand?: string;
  /** Code assigned by the flight data processing system. */
  squawk?: string;
  /** Deliberate flight plan error the delivery controller should notice. */
  error?: 'RFL_PARITY' | 'WRONG_EXIT';
  remarks?: string;
}

/** Callsign spoken: 'Lufthansa four Tango Kilo'; registrations 'Delta Echo Alfa Bravo Charlie'. */
export function sayCallsign(fp: Pick<FlightPlan, 'telephony' | 'flightId'>, abbreviated = false): string {
  if (!fp.telephony) {
    const reg = fp.flightId.replace('-', '');
    if (abbreviated && reg.length >= 4) return [reg[0]!, ...reg.slice(-2)].map(c => ALPHABET[c] ?? c).join(' ');
    return spell(reg);
  }
  return `${fp.telephony} ${spell(fp.flightId)}`;
}
/** Callsign as written on a strip or label: 'DLH4TK', 'DEABC'. */
export const showCallsign = (fp: FlightPlan): string => fp.callsign;

/** Semicircular rule (SERA.5015 / ICAO Annex 2 App. 3): magnetic track 000–179 odd levels, 180–359 even. */
export function correctFL(fl: number, magTrack: number): number {
  const eastbound = magTrack >= 0 && magTrack < 180;
  let f = Math.round(fl / 10);
  if (eastbound ? f % 2 === 0 : f % 2 === 1) f += fl > 300 ? -1 : 1;
  return f * 10;
}
export function flParityOk(fl: number, magTrack: number): boolean {
  return correctFL(fl, magTrack) === Math.round(fl / 10) * 10;
}
